import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { KpiNumber } from "@/components/ui/kpi-number";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import {
  StandardizedTable,
  formatCurrency,
  formatNumber,
} from "@/components/ui/standardized-table";
import GlobalFilter from "@/components/common/GlobalFilter";
import CampaignNameFiltersModal from "@/components/common/CampaignNameFiltersModal";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import {
  Search,
  DollarSign,
  Eye,
  MousePointer,
  TrendingUp,
  TrendingDown,
  Target,
  Star,
  RefreshCw,
  AlertCircle,
  Download,
  Calendar,
  Filter,
  BarChart3,
  Activity,
  Award,
  Zap,
  Info,
  ArrowLeft,
  Settings,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  LineChart as TremorLineChart,
  BarChart as TremorBarChart,
  DonutChart as TremorDonutChart,
  AreaChart as TremorAreaChart,
} from "@tremor/react";
import { ScrollableChart } from "@/components/ui/scrollable-chart";
import { useToast } from "@/hooks/use-toast";
import { useIntegrationStatus } from "@/hooks/useIntegrationStatus";
import {
  GoogleAdsConnectionStatus,
  GoogleAdsCampaign,
  GoogleAdsKeyword,
  GoogleAdsHourlyMetricsResponse,
  MetaAdsConnectionStatus,
  MetaAdsCompleteResponse,
  MetaAdsHourlyPerformanceItem,
} from "@/backend-sdk/client";
import {
  getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet,
  getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet,
  getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet,
  getGoogleAdsKeywordsApiV1IntegrationsGoogleAdsKeywordsGet,
  getGoogleAdsMetricsApiV1IntegrationsGoogleAdsMetricsGet,
  getGoogleAdsHourlyMetricsApiV1IntegrationsGoogleAdsHourlyMetricsGet,
  getGoogleAdsDeviceMetricsApiV1IntegrationsGoogleAdsDeviceMetricsGet,
  getGoogleAdsCityMetricsApiV1IntegrationsGoogleAdsCityMetricsGet,
  getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet,
  getAccountsApiV1IntegrationsMetaAdsAccountsGet,
  getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet,
  getMetaAdsHourlyMetricsApiV1IntegrationsMetaAdsHourlyMetricsGet,
  getMetaAdsPlatformMetricsApiV1IntegrationsMetaAdsPlatformMetricsGet,
} from "@/backend-sdk/client";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { formatSignedPercent } from "@/utils/numberFormat";

// Tipos para filtros e métricas avançadas
type DateRange =
  | "today"
  | "yesterday"
  | "7days"
  | "30days"
  | "90days"
  | "custom";

interface DateFilter {
  range: DateRange;
  startDate: string;
  endDate: string;
  label: string;
}

/** Os oito cards leem só a mídia da aba escolhida. */
const FONTES_DA_PLATAFORMA: Readonly<
  Record<AdsPlatform, readonly RequisitoDeFonte[]>
> = {
  google: ["google_ads"],
  meta: ["meta_ads"],
};

interface AdvancedMetrics {
  totalCost: number;
  totalImpressions: number;
  totalClicks: number;
  totalConversions: number;
  averageCtr: number;
  averageCpc: number;
  averageRoas: number;
  averageQualityScore: number;
  averagePosition: number;
  topPerformingCampaign: string;
  topPerformingKeyword: string;
}

type MetricComparison = {
  value: number;
  percentage: number;
  trend: "up" | "down" | "stable";
};

interface ComparisonMetrics {
  current: Record<string, number>;
  previous: Record<string, number>;
  changes: Record<string, MetricComparison>;
}

interface AdsAccountOption {
  account_id: string;
  account_name: string;
  customer_id?: string;
  currency_code?: string;
  timezone?: string;
  is_active: boolean;
  last_sync_at?: string | null;
}

interface CampaignRow extends Omit<
  GoogleAdsCampaign,
  "impressions" | "clicks" | "cost" | "conversions" | "ctr"
> {
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  name: string;
  type: string;
  status: string;
  budget: number;
  cpc: number;
  roas: number;
  qualityScore: number;
  position: number;
  impressionShare: number;
  impressoes: number;
  cliques: number;
  conversoes: number;
}

interface KeywordRow extends Omit<
  GoogleAdsKeyword,
  "impressions" | "clicks" | "cost" | "ctr" | "average_cpc" | "quality_score"
> {
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
  cpc: number;
  quality_score: number;
  qualityScore: number;
  average_position: number;
  palavra: string;
  impressoes: number;
  cliques: number;
  conversoes: number;
  roas: number;
  posicao: number;
}

interface GeneratedInsight {
  type: "warning" | "success" | "info" | "alert";
  title: string;
  description: string;
  action: string;
  impact: string;
  icon: LucideIcon;
  color: string;
}

type AdsPlatform = "google" | "meta";

interface PerformancePoint {
  name: string;
  impressoes: number;
  cliques: number;
  custo?: number;
  gasto?: number;
  conversoes?: number;
  ctr?: number;
  cpc?: number;
  position?: number;
  roas?: number;
  qualityScore?: number;
}

interface HourlyPerformancePoint {
  hour: string;
  impressoes?: number;
  cliques?: number;
  custo?: number;
  conversoes?: number;
}

interface DevicePerformancePoint {
  device: string;
  cliques: number;
  percentage: number;
}

interface LocationPerformancePoint {
  location: string;
  impressoes: number;
  cliques: number;
  conversoes: number;
  revenue: number;
}

interface NotificationItem {
  id: number;
  type: "success" | "warning";
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

/**
 * `YYYY-MM-DD` → `dd/MM`, sem passar por `Date`. O `date` de `GET /google-ads/metrics` é dia
 * de calendário (M-196), e `new Date("2026-08-01")` o leria como meia-noite UTC — a véspera
 * em São Paulo.
 */
const rotuloDoDia = (dia: string): string => {
  const partes = /^\d{4}-(\d{2})-(\d{2})/.exec(dia);
  return partes === null ? dia : `${partes[2]}/${partes[1]}`;
};

interface DailyMetric {
  date?: string | null;
  hour?: string | number | null;
  impressions?: number | null;
  clicks?: number | null;
  cost?: number | null;
  conversions?: number | null;
  ctr?: number | null;
  average_cpc?: number | null;
  average_position?: number | null;
  total_impressions?: number | null;
  total_reach?: number | null;
  conversion_value?: number | null;
}

interface DateWindow {
  startDate: string;
  endDate: string;
}

interface CampaignDisplayRow {
  campaign?: string;
  campaign_type?: string;
  __campaign: CampaignRow;
}

const toCampaignRowFromGoogle = (campaign: GoogleAdsCampaign): CampaignRow => {
  const impressions = Number(campaign.impressions || 0);
  const clicks = Number(campaign.clicks || 0);
  const cost = Number(campaign.cost || 0);
  const conversions = Number(campaign.conversions || 0);
  const ctr = Number(campaign.ctr || 0) * 100;
  const cpc = Number(campaign.average_cpc || 0);

  return {
    ...campaign,
    impressions,
    clicks,
    cost,
    conversions,
    ctr,
    name: campaign.campaign_name,
    type: campaign.campaign_type,
    status: campaign.campaign_status,
    budget: Number(campaign.budget_amount || 0),
    cpc,
    roas: cost > 0 ? conversions / cost : 0,
    qualityScore: 0,
    position: 0,
    impressionShare: 0,
    impressoes: impressions,
    cliques: clicks,
    conversoes: conversions,
  };
};

const toCampaignRowsFromMeta = (
  metrics: MetaAdsCompleteResponse["daily_metrics"],
): CampaignRow[] => {
  const rows = new Map<string, CampaignRow>();

  for (const metric of metrics) {
    const campaignId = metric.campaign_id ?? "meta-campaign";
    const current =
      rows.get(campaignId) ??
      ({
        campaign_id: campaignId,
        campaign_name: campaignId,
        campaign_type: "META",
        campaign_status: "ENABLED",
        impressions: 0,
        clicks: 0,
        cost: 0,
        conversions: 0,
        ctr: 0,
        name: campaignId,
        type: "META",
        status: "ENABLED",
        budget: 0,
        cpc: 0,
        roas: 0,
        qualityScore: 0,
        position: 0,
        impressionShare: 0,
        impressoes: 0,
        cliques: 0,
        conversoes: 0,
      } satisfies CampaignRow);

    current.impressions += Number(metric.impressions || 0);
    current.clicks += Number(metric.clicks || 0);
    current.cost += Number(metric.cost || 0);
    current.conversions += Number(metric.conversions || 0);
    current.impressoes = current.impressions;
    current.cliques = current.clicks;
    current.conversoes = current.conversions;
    current.ctr =
      current.impressions > 0
        ? (current.clicks / current.impressions) * 100
        : 0;
    current.cpc = current.clicks > 0 ? current.cost / current.clicks : 0;
    current.roas =
      current.cost > 0
        ? Number(metric.conv_value ?? metric.purchase_value ?? 0) / current.cost
        : 0;

    rows.set(campaignId, current);
  }

  return Array.from(rows.values());
};

const toKeywordRowFromGoogle = (keyword: GoogleAdsKeyword): KeywordRow => {
  const impressions = Number(keyword.impressions || 0);
  const clicks = Number(keyword.clicks || 0);
  const cost = Number(keyword.cost || 0);
  const ctr = Number(keyword.ctr || 0) * 100;
  const cpc = Number(keyword.average_cpc || 0);
  const qualityScore = Number(keyword.quality_score || 0);
  const averagePosition = Number(keyword.average_position || 0);

  return {
    ...keyword,
    impressions,
    clicks,
    cost,
    conversions: 0,
    ctr,
    average_cpc: cpc,
    cpc,
    quality_score: qualityScore,
    qualityScore,
    average_position: averagePosition,
    palavra: keyword.keyword_text,
    impressoes: impressions,
    cliques: clicks,
    conversoes: 0,
    roas: 0,
    posicao: averagePosition,
  };
};

const GoogleAdsPage = () => {
  const { toast } = useToast();

  // Estado para controlar a plataforma selecionada
  const [selectedPlatform, setSelectedPlatform] =
    useState<AdsPlatform>("google");

  // Estado para o filtro global
  const { filter: filterState } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filterState);

  // Hook de health monitoring das integrações
  const {
    health,
    status: integrationStatus,
    isLoading: healthLoading,
    forceCheck,
  } = useIntegrationStatus(
    selectedPlatform === "google" ? "google_ads" : "meta_ads",
    selectedPlatform === "google" ? "google-ads-main" : "meta-ads-main",
  );

  const [status, setStatus] = useState<
    | GoogleAdsConnectionStatus
    | (MetaAdsConnectionStatus & { last_sync_at?: string | null })
    | null
  >(null);
  const [accounts, setAccounts] = useState<AdsAccountOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [keywords, setKeywords] = useState<KeywordRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [loading, setLoading] = useState(false);
  // Começa ligado: a busca inicial espera um segundo, e nesse intervalo os oito cards
  // desenhavam zero como se fosse o resultado.
  const [loadingData, setLoadingData] = useState(true);
  // A busca das campanhas falhou: os totais somam uma lista esvaziada, e o zero não foi medido.
  const [erroDados, setErroDados] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [realPerformanceData, setRealPerformanceData] = useState<
    PerformancePoint[]
  >([]);
  const [hourlyPerformance, setHourlyPerformance] = useState<
    HourlyPerformancePoint[]
  >([]);
  const [advancedMetrics, setAdvancedMetrics] =
    useState<AdvancedMetrics | null>(null);
  const [comparisonData, setComparisonData] =
    useState<ComparisonMetrics | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateFilter>({
    range: "30days",
    startDate: "",
    endDate: "",
    label: "Últimos 30 dias",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<string>("all");
  const [insights, setInsights] = useState<GeneratedInsight[]>([]);
  const [viewMode, setViewMode] = useState<
    "overview" | "campaign-detail" | "keywords-detail"
  >("overview");
  const [selectedCampaignDetail, setSelectedCampaignDetail] =
    useState<CampaignRow | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [showCampaignNameFilters, setShowCampaignNameFilters] = useState(false);

  // Estados para filtros avançados
  const [showCampaignFilters, setShowCampaignFilters] = useState(false);
  const [campaignFilters, setCampaignFilters] = useState({
    status: "all", // all, active, paused, removed
    type: "all", // all, search, display, shopping, video
    minCtr: "",
    maxCpc: "",
    minRoas: "",
  });

  // Estados para visualização
  const [campaignViewMode, setCampaignViewMode] = useState<
    "table" | "cards" | "chart"
  >("table");

  // Estados para keywords
  const [allKeywords, setAllKeywords] = useState<KeywordRow[]>([]);
  const [keywordFilters, setKeywordFilters] = useState({
    search: "",
    minQualityScore: "",
    maxCpc: "",
    sortBy: "impressions", // impressions, clicks, ctr, cpc, position
    sortOrder: "desc", // asc, desc
  });

  const customerUuid = localStorage.getItem("active_customer_uuid");

  // Componentes de Loading Skeleton
  const ChartSkeleton = ({ height = 300 }: { height?: number }) => (
    <div className="p-4">
      <Skeleton className={`w-full h-[${height}px]`} />
    </div>
  );

  const InsightsSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 p-4 border rounded-lg">
          <Skeleton className="h-5 w-5 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-6 w-16" />
            </div>
            <Skeleton className="h-3 w-full mt-2" />
            <Skeleton className="h-8 w-24 mt-2" />
          </div>
        </div>
      ))}
    </div>
  );

  // REMOVIDO: Todos os dados mockados (mockCampaigns, mockKeywords, mockInsights, mockPerformanceData)
  // O frontend agora usa apenas dados reais da API

  // Funções utilitárias

  // Função para visualizar detalhes de uma campanha
  const handleViewCampaignDetail = (campaign: CampaignRow) => {
    setSelectedCampaignDetail(campaign);
    setViewMode("campaign-detail");
  };

  const handleBackToOverview = () => {
    setViewMode("overview");
    setSelectedCampaignDetail(null);
  };

  // Função para carregar dados de uma campanha individual
  const getCampaignDetailData = (campaign: CampaignRow) => {
    // Retornar states que já foram carregados ou arrays vazios
    // Os dados serão carregados assincronamente em useEffect
    return {
      hourlyData: hourlyPerformance || [],
      deviceData: [] as DevicePerformancePoint[], // Será carregado em useEffect
      locationData: [] as LocationPerformancePoint[], // Será carregado em useEffect
    };
  };

  // Opções de filtro de data
  const dateRangeOptions: { [key in DateRange]: string } = {
    today: "Hoje",
    yesterday: "Ontem",
    "7days": "Últimos 7 dias",
    "30days": "Últimos 30 dias",
    "90days": "Últimos 90 dias",
    custom: "Período customizado",
  };

  // Função para calcular datas baseado no range
  const calculateDateRange = (
    range: DateRange,
  ): { startDate: string; endDate: string } => {
    const today = new Date();
    const endDate = new Date(today);
    const startDate = new Date(today);

    switch (range) {
      case "today":
        break;
      case "yesterday":
        startDate.setDate(startDate.getDate() - 1);
        endDate.setDate(endDate.getDate() - 1);
        break;
      case "7days":
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "30days":
        startDate.setDate(startDate.getDate() - 30);
        break;
      case "90days":
        startDate.setDate(startDate.getDate() - 90);
        break;
      default:
        break;
    }

    return {
      startDate: formatDateToISO(startDate),
      endDate: formatDateToISO(endDate),
    };
  };

  // Dados de performance e distribuição vazios (sem fallback)
  const performanceData: PerformancePoint[] = [];
  const qualityScoreDistribution: Array<{ score: string; count: number }> = [];

  // REMOVIDO: hourlyPerformance constante - agora usando state gerenciado

  // REMOVIDO: mockInsights - agora usando insights gerados dinamicamente a partir de dados reais

  // Carregar dados na inicialização
  useEffect(() => {
    // Roda só na montagem. A guarda de "já está carregando" saiu: `loadingData` agora nasce
    // ligado, e ela impediria a primeira busca.
    // Aguardar um pouco para garantir que a autenticação está pronta
    const timer = setTimeout(() => {
      loadData();
    }, 1000); // Aguardar 1 segundo

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualizar datas quando range muda
  useEffect(() => {
    if (selectedDateRange.range !== "custom") {
      const dates = calculateDateRange(selectedDateRange.range);
      setSelectedDateRange((prev) => ({
        ...prev,
        startDate: dates.startDate,
        endDate: dates.endDate,
      }));
    }
  }, [selectedDateRange.range]);

  // Gerar notificações quando dados importantes mudam
  useEffect(() => {
    if (campaigns.length > 0 && advancedMetrics) {
      const newNotifications = generateNotifications(advancedMetrics, null);
      if (newNotifications.length > 0) {
        setNotifications((prev) => [...newNotifications, ...prev].slice(0, 10)); // Manter apenas as 10 mais recentes
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, advancedMetrics, comparisonData]);

  // Carregar todas as keywords quando entrar na view de detalhes
  useEffect(() => {
    if (viewMode === "keywords-detail" && allKeywords.length === 0) {
      loadAllKeywords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Carregar hourly data quando range muda ou quando está em overview
  useEffect(() => {
    if (selectedAccount && viewMode === "overview") {
      loadHourlyData(selectedAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, selectedDateRange.range, viewMode]);

  // Carregar hourly data de campanha específica quando entra em detalhes
  useEffect(() => {
    if (
      viewMode === "campaign-detail" &&
      selectedCampaignDetail &&
      selectedAccount
    ) {
      const campaignId = selectedCampaignDetail.campaign_id;
      loadHourlyData(selectedAccount, campaignId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedCampaignDetail, selectedAccount]);

  // Recarregar dados quando mudar a plataforma
  useEffect(() => {
    if (selectedAccount) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform]);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadingData(true);

      // Verificar status da conexão
      const connectionStatus =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsStatusApiV1IntegrationsGoogleAdsStatusGet({
                throwOnError: true,
              })
            ).data
          : (
              await getConnectionStatusApiV1IntegrationsMetaAdsConnectionStatusGet(
                { throwOnError: true },
              )
            ).data;
      setStatus(connectionStatus);

      // MUDANÇA: Sempre carregar dados do banco, independente da conexão

      try {
        // Carregar contas
        const accountsData =
          selectedPlatform === "google"
            ? (
                await getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet(
                  { throwOnError: true },
                )
              ).data.map((account) => ({
                account_id: account.account_id,
                account_name: account.account_name,
                customer_id: account.customer_id,
                currency_code: account.currency_code ?? undefined,
                timezone: account.timezone ?? undefined,
                is_active: account.is_active,
                last_sync_at: account.last_sync_at,
              }))
            : (
                await getAccountsApiV1IntegrationsMetaAdsAccountsGet({
                  throwOnError: true,
                })
              ).data.map((account) => ({
                account_id: account.external_account_id,
                account_name: account.name ?? account.external_account_id,
                customer_id: undefined,
                currency_code: account.currency_code ?? undefined,
                timezone: account.timezone ?? undefined,
                is_active: account.is_active,
                last_sync_at: account.last_sync_at,
              }));
        setAccounts(accountsData);

        if (accountsData.length > 0) {
          const firstAccount = accountsData[0];

          // Se não há conta selecionada, selecionar a primeira e carregar dados
          if (!selectedAccount) {
            setSelectedAccount(firstAccount.account_id);

            toast({
              title: "Carregando Dados",
              description: "Buscando campanhas e métricas do banco de dados...",
            });

            await loadAccountData(firstAccount.account_id);

            toast({
              title: "Dados Carregados",
              description: "Campanhas e métricas carregadas com sucesso!",
            });
          } else {
            // Se já há conta selecionada, apenas recarregar os dados
            await loadAccountData(selectedAccount);
          }
        } else {
          // Mesmo sem contas conectadas, limpar dados
          setCampaigns([]);
          setKeywords([]);
        }
      } catch (apiError) {
        // Em caso de erro, limpar dados
        setErroDados(true);
        setCampaigns([]);
        setKeywords([]);
        setRealPerformanceData([]);
      }

      // Gerar insights dinâmicos baseados nos dados reais
      generateDynamicInsights();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do Google Ads",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setLoadingData(false);
      setLoadingMetrics(false);
    }
  };

  const loadAccountData = async (accountId: string) => {
    try {
      setLoadingData(true);

      // MUDANÇA: Sempre tentar carregar dados do banco

      const mappedCampaigns =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsCampaignsApiV1IntegrationsGoogleAdsCampaignsGet(
                { query: { account_id: accountId }, throwOnError: true },
              )
            ).data.map(toCampaignRowFromGoogle)
          : toCampaignRowsFromMeta(
              (
                await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
                  query: {
                    account_id: accountId,
                    start_date: selectedDateRange.startDate,
                    end_date: selectedDateRange.endDate,
                  },
                  throwOnError: true,
                })
              ).data.daily_metrics,
            );

      setCampaigns(mappedCampaigns);
      setErroDados(false);

      // Carregar palavras-chave reais (apenas para Google Ads)
      const keywordsData =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsKeywordsApiV1IntegrationsGoogleAdsKeywordsGet({
                query: { account_id: accountId },
                throwOnError: true,
              })
            ).data
          : [];

      // Mapear keywords reais
      const mappedKeywords = keywordsData
        .slice(0, 10)
        .map(toKeywordRowFromGoogle);

      setKeywords(mappedKeywords);

      // Carregar métricas reais dos últimos 30 dias
      await loadRealMetricsData(accountId);

      // Calcular métricas avançadas com dados reais
      calculateRealAdvancedMetrics(mappedCampaigns, mappedKeywords);
    } catch (error) {
      // Em caso de erro, limpar dados
      setErroDados(true);
      setCampaigns([]);
      setKeywords([]);
      setRealPerformanceData([]);

      toast({
        title: "Erro",
        description: "Erro ao carregar dados da API do Google Ads",
        variant: "destructive",
      });
    } finally {
      setLoadingData(false);
    }
  };

  // Função para gerar insights dinâmicos baseados em dados reais
  const generateDynamicInsights = () => {
    const dynamicInsights: GeneratedInsight[] = [];

    if (campaigns.length > 0) {
      // Verificar campanhas com CPC alto
      const highCpcCampaigns = campaigns.filter((c) => c.cpc > 10);
      if (highCpcCampaigns.length > 0) {
        dynamicInsights.push({
          type: "warning",
          title: "CPC Alto Detectado",
          description: `${highCpcCampaigns.length} campanha(s) com CPC acima de R$ 10,00`,
          action: "Revisar lances",
          impact: "Alto",
          icon: AlertCircle,
          color: "text-orange-600",
        });
      }

      // Verificar campanhas com bom ROAS
      const goodRoasCampaigns = campaigns.filter((c) => c.roas > 4);
      if (goodRoasCampaigns.length > 0) {
        dynamicInsights.push({
          type: "success",
          title: "Performance Excelente",
          description: `${goodRoasCampaigns.length} campanha(s) com ROAS acima de 4.0`,
          action: "Aumentar orçamento",
          impact: "Alto",
          icon: TrendingUp,
          color: "text-green-600",
        });
      }

      // Verificar campanhas pausadas
      const pausedCampaigns = campaigns.filter((c) => c.status === "PAUSED");
      if (pausedCampaigns.length > 0) {
        dynamicInsights.push({
          type: "info",
          title: "Campanhas Pausadas",
          description: `${pausedCampaigns.length} campanha(s) pausada(s) podem estar perdendo oportunidades`,
          action: "Revisar status",
          impact: "Médio",
          icon: Info,
          color: "text-blue-600",
        });
      }
    }

    if (keywords.length > 0) {
      // Verificar keywords com baixo Quality Score
      const lowQualityKeywords = keywords.filter((k) => k.quality_score < 5);
      if (lowQualityKeywords.length > 0) {
        dynamicInsights.push({
          type: "alert",
          title: "Quality Score Baixo",
          description: `${lowQualityKeywords.length} palavra(s)-chave com Quality Score baixo`,
          action: "Otimizar anúncios",
          impact: "Médio",
          icon: Zap,
          color: "text-yellow-600",
        });
      }
    }

    setInsights(dynamicInsights);
  };

  const loadRealMetricsData = async (accountId: string) => {
    try {
      setLoadingMetrics(true);
      const dates = calculateDateRange(selectedDateRange.range);
      const metricsData =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsMetricsApiV1IntegrationsGoogleAdsMetricsGet({
                query: {
                  account_id: accountId,
                  start_date: dates.startDate,
                  end_date: dates.endDate,
                },
                throwOnError: true,
              })
            ).data
          : (
              await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
                query: {
                  account_id: accountId,
                  start_date: dates.startDate,
                  end_date: dates.endDate,
                },
                throwOnError: true,
              })
            ).data;

      // Converter dados reais para formato do gráfico
      const chartData = metricsData.daily_metrics
        ? metricsData.daily_metrics.map((metric: DailyMetric) => ({
            name: rotuloDoDia(metric.date ?? dates.startDate),
            impressoes: Number(metric.impressions || 0),
            cliques: Number(metric.clicks || 0),
            custo: Number(metric.cost || 0),
            conversoes: Number(metric.conversions || 0),
            ctr: Number(metric.ctr || 0) * 100,
            cpc: Number(metric.average_cpc || 0),
            position: Number(metric.average_position || 0),
          }))
        : [];

      setRealPerformanceData(chartData);

      // Criar dados de performance por hora (simulados a partir dos dados diários)
      const hourlyData = generateHourlyDataFromDaily(chartData);
      setHourlyPerformance(hourlyData);
    } catch (error) {
      // Não falhar o carregamento por causa das métricas
      // Apenas usar dados vazios para os gráficos
      setRealPerformanceData([]);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const calculateRealAdvancedMetrics = (
    campaigns: CampaignRow[],
    keywords: KeywordRow[],
  ) => {
    try {
      // Calcular métricas totais dos dados reais
      const totalImpressions = campaigns.reduce(
        (sum, c) => sum + (c.impressions || 0),
        0,
      );
      const totalClicks = campaigns.reduce(
        (sum, c) => sum + (c.clicks || 0),
        0,
      );
      const totalCost = campaigns.reduce((sum, c) => sum + (c.cost || 0), 0);
      const totalConversions = campaigns.reduce(
        (sum, c) => sum + (c.conversions || 0),
        0,
      );
      const totalConversionValue = campaigns.reduce(
        (sum, c) => sum + (c.conversions * 850 || 0),
        0,
      ); // Valor médio por conversão

      const avgCtr =
        totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
      const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
      const avgQualityScore =
        campaigns.reduce((sum, c) => sum + (c.qualityScore || 0), 0) /
          campaigns.length || 0;
      const avgPosition =
        campaigns.reduce((sum, c) => sum + (c.position || 0), 0) /
          campaigns.length || 0;
      const roas = totalCost > 0 ? totalConversionValue / totalCost : 0;

      const realAdvancedMetrics: AdvancedMetrics = {
        totalCost: totalCost,
        totalImpressions: totalImpressions,
        totalClicks: totalClicks,
        totalConversions: totalConversions,
        averageCtr: avgCtr,
        averageCpc: avgCpc,
        averageRoas: roas,
        averageQualityScore: avgQualityScore,
        averagePosition: avgPosition,
        topPerformingCampaign:
          campaigns.length > 0
            ? campaigns.reduce((best, current) =>
                (current.roas || 0) > (best.roas || 0) ? current : best,
              ).name
            : "",
        topPerformingKeyword:
          keywords.length > 0
            ? keywords.reduce((best, current) =>
                (current.roas || 0) > (best.roas || 0) ? current : best,
              ).keyword_text
            : "",
      };

      setAdvancedMetrics(realAdvancedMetrics);

      // Remover dados de comparação simulados - usar apenas dados reais quando disponíveis
      setComparisonData(null);
    } catch (error) {
      console.error("Erro ao calcular métricas avançadas reais:", error);
      // Sem dados para processar
      setAdvancedMetrics(null);
    }
  };

  const generateHourlyDataFromDaily = (
    dailyData: PerformancePoint[],
  ): HourlyPerformancePoint[] => {
    // Não gerar dados sintéticos - retornar array vazio
    return [];
  };

  const loadHourlyData = async (accountId: string, campaignId?: string) => {
    try {
      const dates = calculateDateRange(selectedDateRange.range);

      const hourlyData =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsHourlyMetricsApiV1IntegrationsGoogleAdsHourlyMetricsGet(
                {
                  query: {
                    account_id: accountId,
                    start_date: dates.startDate,
                    end_date: dates.endDate,
                    campaign_id: campaignId,
                  },
                  throwOnError: true,
                },
              )
            ).data
          : (
              await getMetaAdsHourlyMetricsApiV1IntegrationsMetaAdsHourlyMetricsGet(
                {
                  query: {
                    account_id: accountId,
                    start_date: dates.startDate,
                    end_date: dates.endDate,
                  },
                  throwOnError: true,
                },
              )
            ).data;

      // Mapear dados para formato do gráfico
      const hourlyMetrics = hourlyData as GoogleAdsHourlyMetricsResponse &
        MetaAdsHourlyPerformanceItem[];
      const chartData =
        selectedPlatform === "google"
          ? (hourlyMetrics.data || []).map((metric: DailyMetric) => ({
              hour: `${metric.hour}:00`,
              impressoes: Number(metric.impressions || 0),
              cliques: Number(metric.clicks || 0),
              custo: Number(metric.cost || 0),
              conversoes: Number(metric.conversions || 0),
            }))
          : (hourlyMetrics || []).map((metric: DailyMetric) => ({
              hour: `${metric.hour}:00`,
              impressoes: Number(metric.total_impressions || 0),
              // <M-168> Sem alcance: o Meta não publica `reach` com o recorte horário, e a
              // rota devolve `null` no lugar da estimativa `impressões ÷ 1,3` de antes.
            }));

      setHourlyPerformance(chartData);
    } catch (error) {
      console.error("Erro ao carregar dados por hora:", error);
      setHourlyPerformance([]);
    }
  };

  const loadDeviceData = async (accountId: string) => {
    try {
      const dates = calculateDateRange(selectedDateRange.range);

      if (selectedPlatform === "google") {
        const deviceData = (
          await getGoogleAdsDeviceMetricsApiV1IntegrationsGoogleAdsDeviceMetricsGet(
            {
              query: {
                account_id: accountId,
                start_date: dates.startDate,
                end_date: dates.endDate,
              },
              throwOnError: true,
            },
          )
        ).data;

        return (deviceData.devices || []).map(
          (device: {
            device_type?: string | null;
            total_clicks?: number | null;
          }) => ({
            device: device.device_type,
            cliques: Number(device.total_clicks || 0),
            percentage: 0, // Será calculado depois
          }),
        );
      } else {
        // Meta Ads usa plataformas (Facebook, Instagram, etc)
        const platformData = (
          await getMetaAdsPlatformMetricsApiV1IntegrationsMetaAdsPlatformMetricsGet(
            {
              query: {
                account_id: accountId,
                start_date: dates.startDate,
                end_date: dates.endDate,
              },
              throwOnError: true,
            },
          )
        ).data;

        return (platformData || []).map(
          (platform: {
            platform?: string | null;
            total_reach?: number | null;
          }) => ({
            device: platform.platform,
            cliques: Number(platform.total_reach || 0), // Meta Ads usa reach em vez de clicks
            percentage: 0, // Será calculado depois
          }),
        );
      }
    } catch (error) {
      console.error(
        "Erro ao carregar dados por dispositivo/plataforma:",
        error,
      );
      return [];
    }
  };

  const loadLocationData = async (accountId: string) => {
    try {
      const dates = calculateDateRange(selectedDateRange.range);

      // Meta Ads não tem dados de cidade, apenas Google Ads
      if (selectedPlatform === "meta") {
        return [];
      }

      const locationData = (
        await getGoogleAdsCityMetricsApiV1IntegrationsGoogleAdsCityMetricsGet({
          query: {
            account_id: accountId,
            start_date: dates.startDate,
            end_date: dates.endDate,
            page: 1,
            page_size: 20,
          },
          throwOnError: true,
        })
      ).data;

      return (locationData.items || []).map(
        (location: {
          city_name?: string | null;
          impressions?: number | null;
          clicks?: number | null;
          conversions?: number | null;
          cost?: number | null;
        }) => ({
          location: location.city_name,
          impressoes: Number(location.impressions || 0),
          cliques: Number(location.clicks || 0),
          conversoes: Number(location.conversions || 0),
          revenue: Number(location.cost || 0),
        }),
      );
    } catch (error) {
      console.error("Erro ao carregar dados por localização:", error);
      return [];
    }
  };

  const loadMetricsData = async (accountId: string) => {
    try {
      const dates = calculateDateRange(selectedDateRange.range);
      const metricsData =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsMetricsApiV1IntegrationsGoogleAdsMetricsGet({
                query: {
                  account_id: accountId,
                  start_date: dates.startDate,
                  end_date: dates.endDate,
                },
                throwOnError: true,
              })
            ).data
          : (
              await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
                query: {
                  account_id: accountId,
                  start_date: dates.startDate,
                  end_date: dates.endDate,
                },
                throwOnError: true,
              })
            ).data;

      // Converter dados para formato do gráfico
      const chartData = (metricsData.daily_metrics ?? []).map(
        (metric: DailyMetric) => ({
          name: rotuloDoDia(metric.date ?? dates.startDate),
          impressoes: Number(metric.impressions || 0),
          cliques: Number(metric.clicks || 0),
          conversoes: Number(metric.conversions || 0),
          gasto: Number(metric.cost || 0),
          roas: Number(metric.conversion_value || 0) / Number(metric.cost || 1),
          qualityScore: Number(metric.average_position || 0),
          position: Number(metric.average_position || 0),
        }),
      );

      setRealPerformanceData(chartData);

      // Carregar dados de comparação (período anterior)
      await loadComparisonData(accountId, dates);
    } catch (error) {
      console.error("Erro ao carregar métricas:", error);
      // Limpar dados em caso de erro
      setRealPerformanceData([]);
    }
  };

  const loadComparisonData = async (
    accountId: string,
    currentDates: DateWindow,
  ) => {
    try {
      // Calcular período anterior com a mesma duração
      const daysDiff = Math.ceil(
        (new Date(currentDates.endDate).getTime() -
          new Date(currentDates.startDate).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      const prevStartDate = new Date(currentDates.startDate);
      const prevEndDate = new Date(currentDates.endDate);

      prevStartDate.setDate(prevStartDate.getDate() - daysDiff);
      prevEndDate.setDate(prevEndDate.getDate() - daysDiff);

      const prevMetrics =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsMetricsApiV1IntegrationsGoogleAdsMetricsGet({
                query: {
                  account_id: accountId,
                  start_date: formatDateToISO(prevStartDate),
                  end_date: formatDateToISO(prevEndDate),
                },
                throwOnError: true,
              })
            ).data
          : (
              await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
                query: {
                  account_id: accountId,
                  start_date: formatDateToISO(prevStartDate),
                  end_date: formatDateToISO(prevEndDate),
                },
                throwOnError: true,
              })
            ).data;

      const currentMetrics =
        selectedPlatform === "google"
          ? (
              await getGoogleAdsMetricsApiV1IntegrationsGoogleAdsMetricsGet({
                query: {
                  account_id: accountId,
                  start_date: currentDates.startDate,
                  end_date: currentDates.endDate,
                },
                throwOnError: true,
              })
            ).data
          : (
              await getCompleteDataApiV1IntegrationsMetaAdsCompleteDataGet({
                query: {
                  account_id: accountId,
                  start_date: currentDates.startDate,
                  end_date: currentDates.endDate,
                },
                throwOnError: true,
              })
            ).data;

      // Calcular variações. A chave é a do card (`getComparisonChange`), e o valor é o
      // campo de `GET /google-ads/metrics` que responde por ela — CTR e CPC saem com o
      // nome de média, e não de total.
      const changes: Record<string, MetricComparison> = {};
      const metrics: Record<string, string> = {
        total_impressions: "total_impressions",
        total_clicks: "total_clicks",
        total_cost: "total_cost",
        total_conversions: "total_conversions",
        total_ctr: "average_ctr",
        total_cpc: "average_cpc",
      };

      const mCurrent = currentMetrics as Record<string, unknown>;
      const mPrev = prevMetrics as Record<string, unknown>;
      Object.entries(metrics).forEach(([metric, field]) => {
        const current = Number(mCurrent[field] || 0);
        const previous = Number(mPrev[field] || 0);
        const change = current - previous;
        const percentage = previous > 0 ? (change / previous) * 100 : 0;

        changes[metric] = {
          value: change,
          percentage: percentage,
          trend: percentage > 0 ? "up" : percentage < 0 ? "down" : "stable",
        };
      });

      setComparisonData({
        current: currentMetrics as unknown as Record<string, number>,
        previous: prevMetrics as unknown as Record<string, number>,
        changes,
      });
    } catch (error) {
      console.error("Erro ao carregar dados de comparação:", error);
    }
  };

  const calculateAdvancedMetrics = (
    campaignsData: CampaignRow[],
    keywordsData: KeywordRow[],
  ) => {
    try {
      const totalCost = campaignsData.reduce(
        (sum, campaign) => sum + Number(campaign.cost || 0),
        0,
      );
      const totalConversions = campaignsData.reduce(
        (sum, campaign) => sum + Number(campaign.conversions || 0),
        0,
      );
      const totalRevenue = 0; // Receita real deve vir do backend

      const avgQualityScore =
        keywordsData.reduce(
          (sum, keyword) => sum + Number(keyword.quality_score || 0),
          0,
        ) / (keywordsData.length || 1);
      const avgPosition =
        keywordsData.reduce(
          (sum, keyword) => sum + Number(keyword.average_position || 0),
          0,
        ) / (keywordsData.length || 1);

      const roas = totalCost > 0 ? totalRevenue / totalCost : 0;
      const costPerConversion =
        totalConversions > 0 ? totalCost / totalConversions : 0;
      const totalImpressions = campaignsData.reduce(
        (sum, campaign) => sum + Number(campaign.impressions || 0),
        0,
      );
      const totalClicks = campaignsData.reduce(
        (sum, campaign) => sum + Number(campaign.clicks || 0),
        0,
      );
      const conversionRate =
        totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;

      setAdvancedMetrics({
        totalCost: totalCost,
        totalImpressions: totalImpressions,
        totalClicks: totalClicks,
        totalConversions: totalConversions,
        averageCtr:
          totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
        averageCpc: totalClicks > 0 ? totalCost / totalClicks : 0,
        averageRoas: roas,
        averageQualityScore: avgQualityScore,
        averagePosition: avgPosition,
        topPerformingCampaign:
          campaignsData.length > 0
            ? campaignsData.reduce((best, current) =>
                (current.roas || 0) > (best.roas || 0) ? current : best,
              ).name
            : "",
        topPerformingKeyword:
          keywordsData.length > 0
            ? keywordsData.reduce((best, current) =>
                (current.roas || 0) > (best.roas || 0) ? current : best,
              ).keyword_text
            : "",
      });
    } catch (error) {
      console.error("Erro ao calcular métricas avançadas:", error);
    }
  };

  const handleAccountChange = (accountId: string) => {
    setSelectedAccount(accountId);
    loadAccountData(accountId);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setSelectedDateRange((prev) => ({
      ...prev,
      range,
      label: dateRangeOptions[range],
    }));

    if (selectedAccount) {
      loadAccountData(selectedAccount);
    }
  };

  // Função mais segura para tentar novamente
  const handleRetry = async () => {
    try {
      // Resetar estados
      setCampaigns([]);
      setKeywords([]);
      setRealPerformanceData([]);
      setHourlyPerformance([]);
      setAdvancedMetrics(null);
      setComparisonData(null);

      toast({
        title: "Recarregando",
        description: "Tentando carregar os dados novamente...",
      });

      // Tentar recarregar tudo
      await loadData();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao recarregar dados. Verifique sua conexão.",
        variant: "destructive",
      });
    }
  };

  const handleExportData = () => {
    try {
      // Usar campanhas filtradas ao invés de todas
      const filteredCampaigns = getFilteredCampaigns();
      const filteredKeywords = getFilteredKeywords();

      // Preparar dados de campanhas para exportação
      const campaignExportData = filteredCampaigns.map((campaign, index) => ({
        ID: index + 1,
        "Nome da Campanha": status?.is_connected
          ? campaign.campaign_name
          : campaign.name,
        Tipo: status?.is_connected ? campaign.campaign_type : campaign.type,
        Status: status?.is_connected
          ? campaign.campaign_status === "ENABLED"
            ? "Ativa"
            : campaign.campaign_status
          : campaign.status,
        Impressões: status?.is_connected
          ? campaign.impressions
          : campaign.impressoes,
        Cliques: status?.is_connected ? campaign.clicks : campaign.cliques,
        "CTR (%)": status?.is_connected
          ? (Number(campaign.ctr || 0) * 100).toFixed(2)
          : campaign.ctr.toFixed(2),
        "CPC (R$)": status?.is_connected
          ? Number(campaign.average_cpc || 0).toFixed(2)
          : campaign.cpc.toFixed(2),
        Conversões: status?.is_connected
          ? campaign.conversions
          : campaign.conversoes,
        "Gasto (R$)": status?.is_connected
          ? Number(campaign.cost || 0).toFixed(2)
          : campaign.budget.toFixed(2),
        ROAS: status?.is_connected
          ? "-"
          : campaign.roas
            ? Number(campaign.roas).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "-",
        "Quality Score": status?.is_connected ? "-" : campaign.qualityScore,
        "Impression Share (%)": status?.is_connected
          ? "-"
          : campaign.impressionShare,
        "Data de Exportação": new Date().toLocaleDateString("pt-BR"),
        Período: `${selectedDateRange.startDate || "N/A"} até ${selectedDateRange.endDate || "N/A"}`,
      }));

      // Preparar dados de palavras-chave para exportação
      const keywordExportData = filteredKeywords
        .slice(0, 50)
        .map((keyword, index) => ({
          ID: index + 1,
          "Palavra-chave": status?.is_connected
            ? keyword.keyword_text
            : keyword.palavra,
          Impressões: status?.is_connected
            ? keyword.impressions
            : keyword.impressoes,
          Cliques: status?.is_connected ? keyword.clicks : keyword.cliques,
          "CTR (%)": status?.is_connected
            ? (Number(keyword.ctr || 0) * 100).toFixed(2)
            : keyword.ctr.toFixed(2),
          "CPC (R$)": status?.is_connected
            ? Number(keyword.average_cpc || 0).toFixed(2)
            : keyword.cpc.toFixed(2),
          "Quality Score": status?.is_connected
            ? keyword.quality_score || "-"
            : keyword.qualityScore,
          "Posição Média": status?.is_connected
            ? keyword.average_position
              ? Number(keyword.average_position).toFixed(1)
              : "-"
            : keyword.posicao,
          Conversões: status?.is_connected ? "-" : keyword.conversoes,
          ROAS: status?.is_connected
            ? "-"
            : keyword.roas
              ? Number(keyword.roas).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "-",
        }));

      // Criar CSV para campanhas
      const campaignCsvContent = [
        Object.keys(campaignExportData[0] || {}).join(","),
        ...campaignExportData.map((row) =>
          Object.values(row)
            .map((value) =>
              typeof value === "string" && value.includes(",")
                ? `"${value}"`
                : value,
            )
            .join(","),
        ),
      ].join("\n");

      // Criar CSV para keywords
      const keywordCsvContent = [
        Object.keys(keywordExportData[0] || {}).join(","),
        ...keywordExportData.map((row) =>
          Object.values(row)
            .map((value) =>
              typeof value === "string" && value.includes(",")
                ? `"${value}"`
                : value,
            )
            .join(","),
        ),
      ].join("\n");

      // Função para download
      const downloadCSV = (content: string, filename: string) => {
        const blob = new Blob(["\uFEFF" + content], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      // Baixar arquivos
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(campaignCsvContent, `google-ads-campanhas-${timestamp}.csv`);

      // Delay para evitar conflito de downloads
      setTimeout(() => {
        downloadCSV(
          keywordCsvContent,
          `google-ads-palavras-chave-${timestamp}.csv`,
        );
      }, 1000);

      toast({
        title: "Exportação Concluída",
        description: `${filteredCampaigns.length} campanhas e ${Math.min(filteredKeywords.length, 50)} palavras-chave exportadas com sucesso!`,
      });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast({
        title: "Erro",
        description: "Erro ao exportar dados. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  // Calcular estatísticas dos dados reais
  const calculateStats = () => {
    const totalImpressions = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.impressions || 0),
      0,
    );
    const totalClicks = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.clicks || 0),
      0,
    );
    const totalCost = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.cost || 0),
      0,
    );
    const totalConversions = campaigns.reduce(
      (sum, campaign) => sum + Number(campaign.conversions || 0),
      0,
    );

    const avgCtr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;

    return {
      totalCost,
      totalImpressions,
      avgCtr,
      avgCpc,
      totalConversions,
      // Sem as métricas avançadas, `null` — o card mostra N/D, e não um zero que ninguém mediu.
      roas: advancedMetrics ? Number(advancedMetrics.averageRoas) : null,
      qualityScore: advancedMetrics
        ? Number(advancedMetrics.averageQualityScore)
        : null,
      averagePosition: advancedMetrics
        ? Number(advancedMetrics.averagePosition)
        : null,
    };
  };

  const realStats = calculateStats();

  // Função para obter mudança comparativa
  const getComparisonChange = (metric: string) => {
    const semComparacao = {
      change: formatSignedPercent(0, 1),
      trend: "stable" as const,
    };
    if (!comparisonData || !comparisonData.changes) return semComparacao;

    const data = comparisonData.changes[metric];
    if (!data || typeof data.percentage !== "number") return semComparacao;

    return {
      change: formatSignedPercent(data.percentage, 1),
      trend: data.trend || ("stable" as const),
    };
  };

  const stats = [
    {
      title: "Gasto Total",
      rawValue: realStats.totalCost,
      format: (n: number) =>
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(n),
      ...getComparisonChange("total_cost"),
      icon: DollarSign,
    },
    {
      title: "Impressões",
      rawValue: realStats.totalImpressions,
      format: (n: number) =>
        new Intl.NumberFormat("pt-BR").format(Math.trunc(n)),
      ...getComparisonChange("total_impressions"),
      icon: Eye,
    },
    {
      title: "CTR",
      rawValue: realStats.avgCtr,
      format: (n: number) => `${n.toFixed(1)}%`,
      ...getComparisonChange("total_ctr"),
      icon: MousePointer,
    },
    {
      title: "CPC Médio",
      rawValue: realStats.avgCpc,
      format: (n: number) =>
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(n),
      ...getComparisonChange("total_cpc"),
      icon: Target,
    },
    {
      title: "ROAS",
      rawValue: realStats.roas,
      format: (n: number) => n.toFixed(1),
      ...getComparisonChange("total_roas"),
      icon: TrendingUp,
    },
    {
      title: "Conversões",
      rawValue: realStats.totalConversions,
      format: (n: number) =>
        new Intl.NumberFormat("pt-BR").format(Math.trunc(n)),
      ...getComparisonChange("total_conversions"),
      icon: Award,
    },
    {
      title: "Quality Score",
      rawValue: realStats.qualityScore,
      format: (n: number) => n.toFixed(1),
      ...getComparisonChange("quality_score"),
      icon: Star,
    },
    {
      title: "Posição Média",
      rawValue: realStats.averagePosition,
      format: (n: number) => n.toFixed(1),
      ...getComparisonChange("average_position"),
      icon: Activity,
    },
  ];

  // Se não tiver dados e não estiver carregando, mostrar mensagem
  if (
    status &&
    !status.is_connected &&
    campaigns.length === 0 &&
    accounts.length === 0 &&
    !loading &&
    !loadingData
  ) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold tracking-tight">
                {selectedPlatform === "google" ? "Google Ads" : "Meta Ads"}
              </h1>

              {/* Seletor de Plataforma */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={selectedPlatform === "google" ? "default" : "ghost"}
                  onClick={() => setSelectedPlatform("google")}
                  className="gap-2"
                >
                  <Search className="h-4 w-4" />
                  Google Ads
                </Button>
                <Button
                  size="sm"
                  variant={selectedPlatform === "meta" ? "default" : "ghost"}
                  onClick={() => setSelectedPlatform("meta")}
                  className="gap-2"
                >
                  <Activity className="h-4 w-4" />
                  Meta Ads
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              {selectedPlatform === "google"
                ? "Análise e otimização das suas campanhas de publicidade no Google"
                : "Análise e otimização das suas campanhas de publicidade no Facebook e Instagram"}
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="bg-red-50 text-red-700">
              Sem Dados
            </Badge>
          </div>
        </div>

        <Card className="p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Nenhum dado encontrado</h3>
          <p className="text-muted-foreground mb-4">
            Não há dados de campanhas disponíveis. Execute o seed ou conecte sua
            conta do {selectedPlatform === "google" ? "Google Ads" : "Meta Ads"}
            .
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => handleRetry()}>Tentar Novamente</Button>
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/dashboard/integrations")}
            >
              Ir para Integrações
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // View de detalhes da campanha individual
  if (viewMode === "campaign-detail" && selectedCampaignDetail) {
    const campaignData = getCampaignDetailData(selectedCampaignDetail);

    return (
      <div className="p-6 space-y-6">
        {/* Header da campanha */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={handleBackToOverview}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  {status?.is_connected
                    ? selectedCampaignDetail.campaign_name
                    : selectedCampaignDetail.name}
                </h1>
                {/* Indicador de plataforma */}
                <Badge variant="outline" className="text-xs">
                  {selectedPlatform === "google" ? "Google Ads" : "Meta Ads"}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                Análise detalhada da campanha •{" "}
                {status?.is_connected
                  ? selectedCampaignDetail.campaign_type
                  : selectedCampaignDetail.type}
              </p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <Badge
              variant={
                (status?.is_connected
                  ? selectedCampaignDetail.campaign_status
                  : selectedCampaignDetail.status) === "ENABLED" ||
                (status?.is_connected
                  ? selectedCampaignDetail.campaign_status
                  : selectedCampaignDetail.status) === "Ativa"
                  ? "default"
                  : "secondary"
              }
              className={
                (status?.is_connected
                  ? selectedCampaignDetail.campaign_status
                  : selectedCampaignDetail.status) === "ENABLED" ||
                (status?.is_connected
                  ? selectedCampaignDetail.campaign_status
                  : selectedCampaignDetail.status) === "Ativa"
                  ? "bg-green-100 text-green-800"
                  : ""
              }
            >
              {status?.is_connected
                ? selectedCampaignDetail.campaign_status === "ENABLED"
                  ? "Ativa"
                  : selectedCampaignDetail.campaign_status
                : selectedCampaignDetail.status}
            </Badge>
            <Button size="sm" variant="outline">
              <Settings className="h-4 w-4 mr-2" />
              Configurar
            </Button>
          </div>
        </div>

        {/* Estatísticas específicas da campanha */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-1 min-w-0">
                <span className="truncate">Impressões</span>
                <HelpTooltip
                  content={getKpiTooltip("Impressões")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.is_connected
                  ? Number(
                      selectedCampaignDetail.impressions || 0,
                    ).toLocaleString()
                  : selectedCampaignDetail.impressoes.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">-</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-1 min-w-0">
                <span className="truncate">CTR</span>
                <HelpTooltip
                  content={getKpiTooltip("CTR")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
              <MousePointer className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.is_connected
                  ? `${(Number(selectedCampaignDetail.ctr || 0) * 100).toFixed(1)}%`
                  : selectedCampaignDetail.ctr}
              </div>
              <p className="text-xs text-muted-foreground">-</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-1 min-w-0">
                <span className="truncate">CPC Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("CPC Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.is_connected
                  ? `R$ ${Number(selectedCampaignDetail.average_cpc || 0).toFixed(2)}`
                  : selectedCampaignDetail.cpc}
              </div>
              <p className="text-xs text-muted-foreground">-</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-1 min-w-0">
                <span className="truncate">ROAS</span>
                <HelpTooltip
                  content={getKpiTooltip("ROAS")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.is_connected
                  ? "-"
                  : selectedCampaignDetail.roas.toFixed(1)}
              </div>
              <p className="text-xs text-muted-foreground">-</p>
            </CardContent>
          </Card>
        </div>

        {/* Gráficos específicos da campanha */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Performance por hora */}
          <Card>
            <CardHeader>
              <CardTitle>Performance por Hora</CardTitle>
              <CardDescription>
                Distribuição de impressões ao longo do dia
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaignData.hourlyData.length > 0 ? (
                <ScrollableChart>
                  <TremorBarChart
                    className="h-72"
                    data={campaignData.hourlyData}
                    index="hour"
                    categories={["impressoes", "cliques"]}
                    colors={["blue", "emerald"]}
                    valueFormatter={(value) => value.toLocaleString("pt-BR")}
                    showLegend={true}
                    showGridLines={true}
                    showAnimation={true}
                  />
                </ScrollableChart>
              ) : (
                <EmptyChart message="Nenhum dado de performance por hora disponível para esta campanha" />
              )}
            </CardContent>
          </Card>

          {/* Performance por dispositivo */}
          <Card>
            <CardHeader>
              <CardTitle>Performance por Dispositivo</CardTitle>
              <CardDescription>
                Distribuição de cliques por tipo de dispositivo
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaignData.deviceData.length > 0 ? (
                <TremorDonutChart
                  className="h-72"
                  data={campaignData.deviceData}
                  index="device"
                  category="cliques"
                  colors={["blue", "emerald", "amber"]}
                  valueFormatter={(value) => value.toLocaleString("pt-BR")}
                  showLabel={true}
                  showAnimation={true}
                />
              ) : (
                <EmptyChart message="Nenhum dado de dispositivos disponível para esta campanha" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance por localização */}
        {campaignData.locationData.length > 0 ? (
          <StandardizedTable
            title="Performance por Localização"
            description="Performance das principais regiões geográficas"
            columns={[
              {
                key: "location",
                header: "Localização",
                type: "text",
                align: "left",
                width: "26ch",
              },
              {
                key: "impressions",
                header: "Impressões",
                type: "number",
                align: "right",
                width: "14ch",
                render: (value) => formatNumber(Number(value || 0)),
              },
              {
                key: "clicks",
                header: "Cliques",
                type: "number",
                align: "right",
                width: "12ch",
                render: (value) => formatNumber(Number(value || 0)),
              },
              {
                key: "ctr",
                header: "CTR",
                type: "percent",
                align: "right",
                width: "10ch",
                render: (value) => {
                  const v = Number(value || 0);
                  return (
                    <span
                      className={
                        v > 3 ? "text-green-600 font-medium" : "text-gray-700"
                      }
                    >
                      {v.toFixed(1)}%
                    </span>
                  );
                },
              },
              {
                key: "conversions",
                header: "Conversões",
                type: "number",
                align: "right",
                width: "12ch",
                render: (value) => formatNumber(Number(value || 0)),
              },
              {
                key: "revenue",
                header: "Receita",
                type: "currency",
                align: "right",
                width: "16ch",
                render: (value) => formatCurrency(Number(value || 0)),
              },
            ]}
            data={campaignData.locationData.map((location) => ({
              location: location.location,
              impressions: Number(location.impressoes || 0),
              clicks: Number(location.cliques || 0),
              ctr:
                Number(location.impressoes || 0) > 0
                  ? (Number(location.cliques || 0) /
                      Number(location.impressoes || 0)) *
                    100
                  : 0,
              conversions: Number(location.conversoes || 0),
              revenue: Number(location.revenue || 0),
            }))}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Performance por Localização</CardTitle>
              <CardDescription>
                Performance das principais regiões geográficas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                <p>Nenhum dado de localização disponível para esta campanha</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recomendações específicas da campanha */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Recomendações para esta Campanha
            </CardTitle>
            <CardDescription>
              Sugestões específicas para otimizar a performance desta campanha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 border rounded-lg bg-green-50">
                <TrendingUp className="h-5 w-5 mt-0.5 text-green-600" />
                <div className="flex-1">
                  <h4 className="font-medium">Aumentar Orçamento</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Esta campanha está performando acima da média. Considere
                    aumentar o orçamento em 20% para capturar mais
                    oportunidades.
                  </p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Aplicar Recomendação
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 border rounded-lg bg-yellow-50">
                <AlertCircle className="h-5 w-5 mt-0.5 text-yellow-600" />
                <div className="flex-1">
                  <h4 className="font-medium">Otimizar Horários</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Performance é melhor entre 14h-18h. Considere ajustar os
                    lances para estes horários.
                  </p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Ver Configuração
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Função para aplicar filtros nas campanhas
  const getFilteredCampaigns = () => {
    // Usar apenas dados reais da API
    let filtered = campaigns;

    // Aplicar filtro de campanha selecionada
    if (selectedCampaign !== "all") {
      filtered = filtered.filter(
        (campaign) => campaign.campaign_id === selectedCampaign,
      );
    }

    // Aplicar filtros avançados
    if (campaignFilters.status !== "all") {
      filtered = filtered.filter((campaign) => {
        if (campaignFilters.status === "active") {
          return campaign.campaign_status === "ENABLED";
        } else if (campaignFilters.status === "paused") {
          return campaign.campaign_status === "PAUSED";
        }
        return true;
      });
    }

    if (campaignFilters.type !== "all") {
      filtered = filtered.filter((campaign) => {
        return campaign.campaign_type
          .toLowerCase()
          .includes(campaignFilters.type.toLowerCase());
      });
    }

    // Filtros numéricos
    if (campaignFilters.minCtr) {
      filtered = filtered.filter((campaign) => {
        const ctr = Number(campaign.ctr || 0);
        return ctr >= parseFloat(campaignFilters.minCtr);
      });
    }

    if (campaignFilters.maxCpc) {
      filtered = filtered.filter((campaign) => {
        const cpc = status?.is_connected
          ? Number(campaign.average_cpc || 0)
          : campaign.cpc;
        return cpc <= parseFloat(campaignFilters.maxCpc);
      });
    }

    if (campaignFilters.minRoas && !status?.is_connected) {
      filtered = filtered.filter((campaign) => {
        return campaign.roas >= parseFloat(campaignFilters.minRoas);
      });
    }

    return filtered;
  };

  // Função para aplicar filtros e ordenação nas keywords
  const getFilteredKeywords = () => {
    // Usar apenas dados reais da API
    let filtered = allKeywords.length > 0 ? allKeywords : keywords;

    // Aplicar filtro de busca
    if (keywordFilters.search) {
      filtered = filtered.filter((keyword) => {
        return keyword.keyword_text
          .toLowerCase()
          .includes(keywordFilters.search.toLowerCase());
      });
    }

    // Aplicar filtros numéricos
    if (keywordFilters.minQualityScore) {
      filtered = filtered.filter((keyword) => {
        const qualityScore = status?.is_connected
          ? Number(keyword.quality_score || 0)
          : keyword.qualityScore;
        return qualityScore >= parseFloat(keywordFilters.minQualityScore);
      });
    }

    if (keywordFilters.maxCpc) {
      filtered = filtered.filter((keyword) => {
        const cpc = status?.is_connected
          ? Number(keyword.average_cpc || 0)
          : keyword.cpc;
        return cpc <= parseFloat(keywordFilters.maxCpc);
      });
    }

    // Aplicar ordenação
    const sortKey = keywordFilters.sortBy;
    const sortOrder = keywordFilters.sortOrder;

    filtered.sort((a, b) => {
      let aValue, bValue;

      if (status?.is_connected) {
        switch (sortKey) {
          case "impressions":
            aValue = Number(a.impressions || 0);
            bValue = Number(b.impressions || 0);
            break;
          case "clicks":
            aValue = Number(a.clicks || 0);
            bValue = Number(b.clicks || 0);
            break;
          case "ctr":
            aValue = Number(a.ctr || 0);
            bValue = Number(b.ctr || 0);
            break;
          case "cpc":
            aValue = Number(a.average_cpc || 0);
            bValue = Number(b.average_cpc || 0);
            break;
          case "position":
            aValue = Number(a.average_position || 0);
            bValue = Number(b.average_position || 0);
            break;
          default:
            aValue = Number(a.impressions || 0);
            bValue = Number(b.impressions || 0);
        }
      } else {
        switch (sortKey) {
          case "impressions":
            aValue = a.impressoes;
            bValue = b.impressoes;
            break;
          case "clicks":
            aValue = a.cliques;
            bValue = b.cliques;
            break;
          case "ctr":
            aValue = a.ctr;
            bValue = b.ctr;
            break;
          case "cpc":
            aValue = a.cpc;
            bValue = b.cpc;
            break;
          case "position":
            aValue = a.posicao;
            bValue = b.posicao;
            break;
          default:
            aValue = a.impressoes;
            bValue = b.impressoes;
        }
      }

      if (sortOrder === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filtered;
  };

  // Função para visualizar todas as keywords
  const handleViewAllKeywords = () => {
    setViewMode("keywords-detail");
    if (allKeywords.length === 0) {
      loadAllKeywords();
    }
  };

  // Função para carregar todas as keywords (não limitadas)
  const loadAllKeywords = async () => {
    if (!selectedAccount) return;

    try {
      setLoading(true);

      if (status?.is_connected && selectedPlatform === "google") {
        // Carregar dados reais da API (apenas Google Ads tem keywords)
        const keywordsData = (
          await getGoogleAdsKeywordsApiV1IntegrationsGoogleAdsKeywordsGet({
            query: { account_id: selectedAccount },
            throwOnError: true,
          })
        ).data;

        const mappedKeywords = keywordsData.map(toKeywordRowFromGoogle);

        setAllKeywords(mappedKeywords);
      } else {
        // Usar apenas keywords existentes sem expansão simulada
        setAllKeywords(keywords);
      }
    } catch (error) {
      console.error("Erro ao carregar todas as keywords:", error);
      // Em caso de erro, usar apenas as keywords já carregadas
      setAllKeywords(keywords);
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar notificações baseadas em mudanças importantes
  const generateNotifications = (
    currentData: AdvancedMetrics,
    previousData: AdvancedMetrics | null,
  ): NotificationItem[] => {
    const newNotifications: NotificationItem[] = [];

    // Verificar mudanças significativas no ROAS
    if (previousData && currentData.averageRoas && previousData.averageRoas) {
      const roasChange =
        ((currentData.averageRoas - previousData.averageRoas) /
          previousData.averageRoas) *
        100;
      if (Math.abs(roasChange) > 10) {
        newNotifications.push({
          id: Date.now(),
          type: roasChange > 0 ? "success" : "warning",
          title:
            roasChange > 0
              ? "ROAS Melhorou Significativamente"
              : "ROAS Diminuiu Significativamente",
          message: `ROAS ${roasChange > 0 ? "aumentou" : "diminuiu"} ${Math.abs(roasChange).toFixed(1)}% comparado ao período anterior`,
          timestamp: new Date(),
          read: false,
        });
      }
    }

    // Verificar campanhas com baixa performance
    const lowPerformingCampaigns = campaigns.filter((campaign) => {
      const ctr = status?.is_connected
        ? Number(campaign.ctr || 0)
        : campaign.ctr;
      return ctr < 1.5; // CTR muito baixo
    });

    if (lowPerformingCampaigns.length > 0) {
      newNotifications.push({
        id: Date.now() + 1,
        type: "warning",
        title: "Campanhas com CTR Baixo",
        message: `${lowPerformingCampaigns.length} campanha(s) com CTR abaixo de 1.5% precisam de atenção`,
        timestamp: new Date(),
        read: false,
      });
    }

    return newNotifications;
  };

  // Componente de notificações
  const NotificationBadge = () => {
    const unreadCount = notifications.filter((n) => !n.read).length;

    if (unreadCount === 0) return null;

    return (
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            toast({
              title: `${unreadCount} nova(s) notificação(ões)`,
              description: notifications.find((n) => !n.read)?.message || "",
            });
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
          }}
        >
          <AlertCircle className="h-4 w-4 mr-2" />
          Notificações
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </Button>
      </div>
    );
  };

  // Função para toggle de visualização de campanhas
  const handleCampaignVisualization = () => {
    const modes: Array<"table" | "cards" | "chart"> = [
      "table",
      "cards",
      "chart",
    ];
    const currentIndex = modes.indexOf(campaignViewMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setCampaignViewMode(modes[nextIndex]);

    toast({
      title: "Visualização Alterada",
      description: `Modo de visualização: ${modes[nextIndex] === "table" ? "Tabela" : modes[nextIndex] === "cards" ? "Cards" : "Gráfico"}`,
    });
  };

  // Função para resetar filtros de campanhas
  const resetCampaignFilters = () => {
    setCampaignFilters({
      status: "all",
      type: "all",
      minCtr: "",
      maxCpc: "",
      minRoas: "",
    });
  };

  // Componente para modal de filtros avançados
  const CampaignFiltersModal = () => {
    if (!showCampaignFilters) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Filtros Avançados</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCampaignFilters(false)}
            >
              ✕
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={campaignFilters.status}
                onChange={(e) =>
                  setCampaignFilters((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Todos</option>
                <option value="active">Ativas</option>
                <option value="paused">Pausadas</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Tipo</label>
              <select
                value={campaignFilters.type}
                onChange={(e) =>
                  setCampaignFilters((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Todos</option>
                <option value="search">Search</option>
                <option value="display">Display</option>
                <option value="shopping">Shopping</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">CTR mínimo (%)</label>
              <input
                type="number"
                placeholder="Ex: 3.0"
                step="0.1"
                value={campaignFilters.minCtr}
                onChange={(e) =>
                  setCampaignFilters((prev) => ({
                    ...prev,
                    minCtr: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium">CPC máximo (R$)</label>
              <input
                type="number"
                placeholder="Ex: 10.00"
                step="0.01"
                value={campaignFilters.maxCpc}
                onChange={(e) =>
                  setCampaignFilters((prev) => ({
                    ...prev,
                    maxCpc: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            {!status?.is_connected && (
              <div>
                <label className="text-sm font-medium">ROAS mínimo</label>
                <input
                  type="number"
                  placeholder="Ex: 3.0"
                  step="0.1"
                  value={campaignFilters.minRoas}
                  onChange={(e) =>
                    setCampaignFilters((prev) => ({
                      ...prev,
                      minRoas: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-md text-sm"
                />
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-6">
            <Button
              variant="outline"
              onClick={resetCampaignFilters}
              className="flex-1"
            >
              Limpar
            </Button>
            <Button
              onClick={() => setShowCampaignFilters(false)}
              className="flex-1"
            >
              Aplicar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // View de detalhes de todas as palavras-chave
  if (viewMode === "keywords-detail") {
    const filteredKeywords = getFilteredKeywords();

    return (
      <div className="p-6 space-y-6">
        {/* Header das palavras-chave */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setViewMode("overview")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Todas as Palavras-chave
              </h1>
              <p className="text-muted-foreground">
                Análise detalhada de performance • {filteredKeywords.length}{" "}
                palavras-chave
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const csvData = filteredKeywords.map((keyword, index) => ({
                  ID: index + 1,
                  "Palavra-chave": status?.is_connected
                    ? keyword.keyword_text
                    : keyword.palavra,
                  Impressões: status?.is_connected
                    ? keyword.impressions
                    : keyword.impressoes,
                  Cliques: status?.is_connected
                    ? keyword.clicks
                    : keyword.cliques,
                  CTR: status?.is_connected ? keyword.ctr : keyword.ctr,
                  CPC: status?.is_connected ? keyword.average_cpc : keyword.cpc,
                  "Quality Score": status?.is_connected
                    ? keyword.quality_score
                    : keyword.qualityScore,
                  Posição: status?.is_connected
                    ? keyword.average_position
                    : keyword.posicao,
                }));

                const csvContent = [
                  Object.keys(csvData[0]).join(","),
                  ...csvData.map((row) => Object.values(row).join(",")),
                ].join("\n");

                const blob = new Blob(["\uFEFF" + csvContent], {
                  type: "text/csv;charset=utf-8;",
                });
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute(
                  "download",
                  `palavras-chave-${new Date().toISOString().split("T")[0]}.csv`,
                );
                link.style.visibility = "hidden";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast({
                  title: "Exportação Concluída",
                  description: `${filteredKeywords.length} palavras-chave exportadas!`,
                });
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>

        {/* Filtros das palavras-chave */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium">
                Buscar palavra-chave
              </label>
              <input
                type="text"
                placeholder="Digite para buscar..."
                value={keywordFilters.search}
                onChange={(e) =>
                  setKeywordFilters((prev) => ({
                    ...prev,
                    search: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium">
                Quality Score mínimo
              </label>
              <input
                type="number"
                placeholder="Ex: 7"
                min="1"
                max="10"
                value={keywordFilters.minQualityScore}
                onChange={(e) =>
                  setKeywordFilters((prev) => ({
                    ...prev,
                    minQualityScore: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium">CPC máximo (R$)</label>
              <input
                type="number"
                placeholder="Ex: 10.00"
                step="0.01"
                value={keywordFilters.maxCpc}
                onChange={(e) =>
                  setKeywordFilters((prev) => ({
                    ...prev,
                    maxCpc: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Ordenar por</label>
              <select
                value={keywordFilters.sortBy}
                onChange={(e) =>
                  setKeywordFilters((prev) => ({
                    ...prev,
                    sortBy: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="impressions">Impressões</option>
                <option value="clicks">Cliques</option>
                <option value="ctr">CTR</option>
                <option value="cpc">CPC</option>
                <option value="position">Posição</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Ordem</label>
              <select
                value={keywordFilters.sortOrder}
                onChange={(e) =>
                  setKeywordFilters((prev) => ({
                    ...prev,
                    sortOrder: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="desc">Maior primeiro</option>
                <option value="asc">Menor primeiro</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setKeywordFilters({
                  search: "",
                  minQualityScore: "",
                  maxCpc: "",
                  sortBy: "impressions",
                  sortOrder: "desc",
                })
              }
            >
              Limpar Filtros
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={loadAllKeywords}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
              />
              Atualizar
            </Button>
          </div>
        </Card>

        {/* Estatísticas resumidas */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1">
                <span className="truncate">Total de Palavras-chave</span>
                <HelpTooltip
                  content="Quantidade de palavras‑chave analisadas (após os filtros). Útil para entender o volume e a cobertura da estratégia de Search."
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2">
              <div className="text-2xl font-bold">
                {filteredKeywords.length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1">
                <span className="truncate">Quality Score Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("Quality Score")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2">
              <div className="text-2xl font-bold">
                {filteredKeywords.length > 0
                  ? (
                      filteredKeywords.reduce(
                        (sum, kw) =>
                          sum +
                          (status?.is_connected
                            ? Number(kw.quality_score || 0)
                            : kw.qualityScore),
                        0,
                      ) / filteredKeywords.length
                    ).toFixed(1)
                  : "0"}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1">
                <span className="truncate">CPC Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("CPC Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2">
              <div className="text-2xl font-bold">
                R${" "}
                {filteredKeywords.length > 0
                  ? (
                      filteredKeywords.reduce((sum, kw) => {
                        const cpc = status?.is_connected
                          ? Number(kw.average_cpc || 0)
                          : kw.cpc;
                        return sum + cpc;
                      }, 0) / filteredKeywords.length
                    ).toFixed(2)
                  : "0.00"}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-1">
                <span className="truncate">Posição Média</span>
                <HelpTooltip
                  content={getKpiTooltip("Posição Média")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2">
              <div className="text-2xl font-bold">
                {filteredKeywords.length > 0
                  ? (
                      filteredKeywords.reduce((sum, kw) => {
                        const pos = status?.is_connected
                          ? Number(kw.average_position || 0)
                          : kw.posicao;
                        return sum + pos;
                      }, 0) / filteredKeywords.length
                    ).toFixed(1)
                  : "0.0"}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabela detalhada de palavras-chave */}
        {/* Enquanto `loadAllKeywords` busca, a tabela aparece com o aviso de carregamento em vez
            do estado vazio. */}
        {filteredKeywords.length > 0 || loading ? (
          <StandardizedTable
            loading={loading}
            title="Palavras-chave Detalhadas"
            description="Performance individual de cada palavra-chave com filtros aplicados"
            columns={[
              {
                key: "rank",
                header: "#",
                type: "number",
                align: "right",
                width: "6ch",
                render: (v) => formatNumber(Number(v || 0)),
              },
              {
                key: "keyword",
                header: "Palavra-chave",
                type: "text",
                align: "left",
                width: "28ch",
                render: (value) => (
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {String(value ?? "")}
                  </span>
                ),
              },
              {
                key: "impressions",
                header: "Impressões",
                type: "number",
                align: "right",
                width: "14ch",
                render: (v) => formatNumber(Number(v || 0)),
              },
              {
                key: "clicks",
                header: "Cliques",
                type: "number",
                align: "right",
                width: "12ch",
                render: (v) => formatNumber(Number(v || 0)),
              },
              {
                key: "ctr",
                header: "CTR",
                type: "percent",
                align: "right",
                width: "10ch",
                render: (v) => {
                  const value = Number(v || 0);
                  return (
                    <span
                      className={
                        value > 3
                          ? "text-green-600 font-medium"
                          : "text-gray-700"
                      }
                    >
                      {value.toFixed(1)}%
                    </span>
                  );
                },
              },
              {
                key: "cpc",
                header: "CPC",
                type: "currency",
                align: "right",
                width: "12ch",
                render: (v) => formatCurrency(Number(v || 0)),
              },
              {
                key: "conversions",
                header: "Conversões",
                type: "number",
                align: "right",
                width: "12ch",
                render: (v) =>
                  v === null || v === undefined
                    ? "-"
                    : formatNumber(Number(v || 0)),
              },
              {
                key: "roas",
                header: "ROAS",
                type: "number",
                align: "right",
                width: "10ch",
                render: (v) =>
                  v === null || v === undefined
                    ? "-"
                    : Number(v || 0).toFixed(1),
              },
              {
                key: "quality_score",
                header: "Quality Score",
                type: "number",
                align: "center",
                width: "14ch",
                render: (v) => {
                  const score = Number(v);
                  if (!Number.isFinite(score)) return "-";
                  return (
                    <div className="flex items-center justify-center gap-1">
                      <span
                        className={`font-medium ${
                          score >= 8
                            ? "text-green-600"
                            : score >= 6
                              ? "text-yellow-600"
                              : "text-red-600"
                        }`}
                      >
                        {score}
                      </span>
                      {score >= 8 && (
                        <Star className="h-3 w-3 text-yellow-500 fill-current" />
                      )}
                    </div>
                  );
                },
              },
              {
                key: "position",
                header: "Posição",
                type: "number",
                align: "right",
                width: "10ch",
                render: (v) => {
                  const value = Number(v);
                  if (!Number.isFinite(value)) return "-";
                  return (
                    <span
                      className={
                        value <= 2
                          ? "text-green-600 font-medium"
                          : "text-gray-700"
                      }
                    >
                      {value.toFixed(1)}
                    </span>
                  );
                },
              },
            ]}
            data={filteredKeywords.map((keyword, index) => {
              const kwText = status?.is_connected
                ? keyword.keyword_text
                : keyword.palavra;
              const impressions = status?.is_connected
                ? Number(keyword.impressions || 0)
                : Number(keyword.impressoes || 0);
              const clicks = status?.is_connected
                ? Number(keyword.clicks || 0)
                : Number(keyword.cliques || 0);

              const ctrPercent = status?.is_connected
                ? Number(keyword.ctr || 0) * 100
                : Number(
                    String(keyword.ctr || "0")
                      .replace("%", "")
                      .replace(",", "."),
                  ) || 0;

              const cpc = status?.is_connected
                ? Number(keyword.average_cpc || 0)
                : Number(
                    String(keyword.cpc || "")
                      .replace(/[^\d,.-]/g, "")
                      .replace(/\./g, "")
                      .replace(",", "."),
                  ) || 0;

              const conversions = status?.is_connected
                ? null
                : Number(keyword.conversoes || 0);
              const roas = status?.is_connected
                ? null
                : Number(keyword.roas || 0);
              const qualityScore = status?.is_connected
                ? Number(keyword.quality_score)
                : Number(keyword.qualityScore);
              const position = status?.is_connected
                ? Number(keyword.average_position || 0)
                : Number(keyword.posicao || 0);

              return {
                rank: index + 1,
                keyword: kwText,
                impressions,
                clicks,
                ctr: ctrPercent,
                cpc,
                conversions,
                roas,
                quality_score: qualityScore,
                position,
              };
            })}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Palavras-chave Detalhadas</CardTitle>
              <CardDescription>
                Performance individual de cada palavra-chave com filtros
                aplicados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <NoResultsState type="keywords" />
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Componente para estado vazio
  const EmptyState = ({
    title,
    description,
    action,
  }: {
    title: string;
    description: string;
    action?: () => void;
  }) => (
    <Card className="p-8 text-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="p-4 bg-gray-100 rounded-full">
          <BarChart3 className="h-8 w-8 text-gray-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-gray-500 mt-2">{description}</p>
        </div>
        {action && (
          <Button onClick={action} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </Button>
        )}
      </div>
    </Card>
  );

  // Componente para estado de dados vazios específico para campanhas
  const EmptyCampaignsState = () => (
    <EmptyState
      title="Nenhuma campanha encontrada"
      description="Não há campanhas ativas na sua conta Google Ads ou os dados ainda não foram sincronizados."
      action={() => handleRetry()}
    />
  );

  // Componente para estado de dados vazios específico para keywords
  const EmptyKeywordsState = () => (
    <EmptyState
      title="Nenhuma palavra-chave encontrada"
      description="Não há palavras-chave nas suas campanhas ou os dados ainda não foram sincronizados."
      action={() => handleRetry()}
    />
  );

  // Componente para exibir mensagem quando filtros não retornam resultados
  function NoResultsState({ type }: { type: "campanhas" | "keywords" }) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>Nenhum(a) {type} encontrado(a) com os filtros aplicados</p>
        <Button
          variant="ghost"
          onClick={() =>
            type === "campanhas"
              ? setCampaignFilters({
                  status: "all",
                  type: "all",
                  minCtr: "",
                  maxCpc: "",
                  minRoas: "",
                })
              : setKeywordFilters({
                  search: "",
                  minQualityScore: "",
                  maxCpc: "",
                  sortBy: "impressions",
                  sortOrder: "desc",
                })
          }
          className="mt-2"
        >
          Limpar Filtros
        </Button>
      </div>
    );
  }

  // Componente para gráficos sem dados
  function EmptyChart({
    message = "Nenhum dado disponível",
  }: {
    message?: string;
  }) {
    return (
      <div className="flex items-center justify-center h-[280px] text-muted-foreground">
        <div className="text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <IntegrationErrorBoundary
      integrationType="google_ads"
      integrationId="google-ads-main"
    >
      <div className="p-6 space-y-6">
        {/* Header com filtros */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold tracking-tight">
                {selectedPlatform === "google" ? "Google Ads" : "Meta Ads"}
              </h1>

              {/* Seletor de Plataforma */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={selectedPlatform === "google" ? "default" : "ghost"}
                  onClick={() => setSelectedPlatform("google")}
                  className="gap-2"
                >
                  <Search className="h-4 w-4" />
                  Google Ads
                </Button>
                <Button
                  size="sm"
                  variant={selectedPlatform === "meta" ? "default" : "ghost"}
                  onClick={() => setSelectedPlatform("meta")}
                  className="gap-2"
                >
                  <Activity className="h-4 w-4" />
                  Meta Ads
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              {selectedPlatform === "google"
                ? "Análise e otimização das suas campanhas de publicidade no Google"
                : "Análise e otimização das suas campanhas de publicidade no Facebook e Instagram"}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Badge
              variant="outline"
              className={
                loadingData || loading
                  ? "bg-blue-50 text-blue-700"
                  : status?.is_connected
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-50 text-gray-700"
              }
            >
              {loadingData || loading
                ? "🔄 Carregando dados..."
                : status?.is_connected
                  ? "Conectado"
                  : "Carregando..."}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCampaignNameFilters(true)}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Filtro de Campanhas
            </Button>
            {status?.last_sync_at && (
              <div className="text-xs text-muted-foreground">
                Última sync:{" "}
                {new Date(status.last_sync_at).toLocaleString("pt-BR")}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  // Forçar reload completo
                  await loadData();
                } catch (error) {
                  console.error(
                    "Erro ao recarregar dados do Google Ads:",
                    error,
                  );
                }
              }}
            >
              🧪 Debug
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedCampaign("all");
                setCampaignFilters({
                  status: "all",
                  type: "all",
                  minCtr: "",
                  maxCpc: "",
                  minRoas: "",
                });
              }}
            >
              🔄 Reset Filtros
            </Button>
            <NotificationBadge />
          </div>
        </div>

        {/* Filtros avançados */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Seletor de conta */}
              {accounts.length > 1 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Conta:</label>
                  <select
                    value={selectedAccount}
                    onChange={(e) => handleAccountChange(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm min-w-[200px]"
                  >
                    {accounts.map((account) => (
                      <option
                        key={account.account_id}
                        value={account.account_id}
                      >
                        {account.account_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Seletor de período */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <select
                  value={selectedDateRange.range}
                  onChange={(e) =>
                    handleDateRangeChange(e.target.value as DateRange)
                  }
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  {Object.entries(dateRangeOptions).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Seletor de campanha */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <select
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm min-w-[150px]"
                >
                  <option value="all">Todas as campanhas</option>
                  {campaigns.map((campaign) => (
                    <option
                      key={campaign.campaign_id || campaign.name}
                      value={campaign.campaign_id || campaign.name}
                    >
                      {campaign.campaign_name || campaign.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleExportData}
                disabled={loading}
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </Card>

        {/* Filtro Global */}
        <GlobalFilter className="mb-6" />

        {/* Stats Cards - Layout fixo em grid responsivo */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card
              key={stat.title}
              className="hover:shadow-md transition-shadow min-h-[120px]"
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="flex items-center gap-1 min-w-0">
                  <span className="truncate">{stat.title}</span>
                  <HelpTooltip
                    content={
                      getKpiTooltip(stat.title) ??
                      (stat.title === "Gasto Total"
                        ? getKpiTooltip("Investimento")
                        : undefined)
                    }
                    triggerClassName="h-3.5 w-3.5 text-[9px]"
                  />
                </CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-col items-start min-w-0">
                  <div className="min-w-0 flex-1">
                    <EstadoDoValor
                      carregando={loadingData}
                      disponivel={
                        !erroDados &&
                        stat.rawValue !== null &&
                        Number.isFinite(stat.rawValue)
                      }
                      fontes={FONTES_DA_PLATAFORMA[selectedPlatform]}
                      erro={erroDados}
                      className="text-2xl font-bold text-muted-foreground"
                    >
                      <KpiNumber
                        value={Number(stat.rawValue ?? 0)}
                        format={stat.format}
                        className="font-bold text-foreground"
                        maxFontPx={28}
                        breathingRoomPx={18}
                        ariaLabel={stat.title}
                      />
                    </EstadoDoValor>
                  </div>
                  {comparisonEnabled && !loadingData && (
                    <div className="flex items-center gap-1 text-xs">
                      {stat.trend === "up" ? (
                        <TrendingUp className="h-3 w-3 text-success-soft flex-shrink-0" />
                      ) : stat.trend === "down" ? (
                        <TrendingDown className="h-3 w-3 text-error-soft flex-shrink-0" />
                      ) : (
                        <div className="h-3 w-3 flex-shrink-0" />
                      )}
                      <span
                        className={`${stat.trend === "up" ? "text-success-soft" : stat.trend === "down" ? "text-error-soft" : "text-gray-500"} whitespace-nowrap`}
                      >
                        {stat.change}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Insights and Recommendations */}
        {insights.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Insights e Recomendações
              </CardTitle>
              <CardDescription>
                Oportunidades identificadas automaticamente para otimizar suas
                campanhas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <InsightsSkeleton />
              ) : (
                <div className="space-y-4">
                  {insights.map((insight, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 border rounded-lg"
                    >
                      <insight.icon
                        className={`h-5 w-5 mt-0.5 ${insight.color}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">{insight.title}</h4>
                          <Badge
                            variant={
                              insight.type === "success"
                                ? "default"
                                : insight.type === "warning"
                                  ? "secondary"
                                  : "outline"
                            }
                          >
                            {insight.impact}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {insight.description}
                        </p>
                        <Button size="sm" variant="outline" className="mt-2">
                          {insight.action}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Charts - Layout responsivo otimizado */}
        <div className="space-y-6">
          {/* Gráfico principal - largura total */}
          <Card>
            <CardHeader>
              <CardTitle>Performance das Campanhas</CardTitle>
              <CardDescription>
                Evolução de impressões, cliques e conversões ao longo do tempo
              </CardDescription>
            </CardHeader>
            <CardContent className="pl-2">
              {loading ? (
                <ChartSkeleton height={350} />
              ) : realPerformanceData.length > 0 ? (
                <ScrollableChart>
                  <TremorBarChart
                    className="h-80"
                    data={realPerformanceData}
                    index="name"
                    categories={["impressoes", "conversoes"]}
                    colors={["blue", "emerald"]}
                    valueFormatter={(value) => value.toLocaleString("pt-BR")}
                    showLegend={true}
                    showGridLines={true}
                    showAnimation={true}
                  />
                </ScrollableChart>
              ) : (
                <EmptyChart message="Nenhum dado de performance disponível" />
              )}
            </CardContent>
          </Card>

          {/* Gráficos secundários - grid 2x2 */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Quality Score Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Distribuição Quality Score</CardTitle>
                <CardDescription>
                  Palavras-chave por faixa de quality score
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <ChartSkeleton height={280} />
                ) : qualityScoreDistribution.length > 0 ? (
                  <TremorDonutChart
                    className="h-72"
                    data={qualityScoreDistribution}
                    index="score"
                    category="count"
                    colors={[
                      "red",
                      "orange",
                      "amber",
                      "yellow",
                      "lime",
                      "green",
                      "emerald",
                      "teal",
                      "cyan",
                      "blue",
                    ]}
                    valueFormatter={(value) => value.toLocaleString("pt-BR")}
                    showLabel={true}
                    showAnimation={true}
                  />
                ) : (
                  <EmptyChart message="Nenhum dado de Quality Score disponível" />
                )}
              </CardContent>
            </Card>

            {/* CTR vs CPC Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Gasto vs Conversões</CardTitle>
                <CardDescription>
                  Relação entre investimento e resultados
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                {loadingData || loadingMetrics ? (
                  <ChartSkeleton height={280} />
                ) : realPerformanceData.length > 0 ? (
                  <ScrollableChart>
                    <TremorLineChart
                      className="h-72"
                      data={realPerformanceData}
                      index="name"
                      categories={["custo", "conversoes"]}
                      colors={["red", "green"]}
                      valueFormatter={(value) => value.toLocaleString("pt-BR")}
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                ) : (
                  <EmptyChart message="Nenhum dado de gasto vs conversões disponível" />
                )}
              </CardContent>
            </Card>

            {/* Hourly Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Performance por Hora</CardTitle>
                <CardDescription>
                  Distribuição de impressões ao longo do dia
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                {loadingData || loadingMetrics ? (
                  <ChartSkeleton height={280} />
                ) : hourlyPerformance.length > 0 ? (
                  <ScrollableChart>
                    <TremorAreaChart
                      className="h-72"
                      data={hourlyPerformance}
                      index="hour"
                      categories={["impressoes"]}
                      colors={["violet"]}
                      valueFormatter={(value) => value.toLocaleString("pt-BR")}
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                ) : (
                  <EmptyChart message="Nenhum dado de performance por hora disponível" />
                )}
              </CardContent>
            </Card>

            {/* ROAS Trend */}
            <Card>
              <CardHeader>
                <CardTitle>Tendência ROAS</CardTitle>
                <CardDescription>
                  Retorno sobre investimento publicitário
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-2">
                {loading ? (
                  <ChartSkeleton height={280} />
                ) : realPerformanceData.length > 0 ? (
                  <ScrollableChart>
                    <TremorLineChart
                      className="h-72"
                      data={realPerformanceData}
                      index="name"
                      categories={["roas"]}
                      colors={["emerald"]}
                      valueFormatter={(value) => value.toLocaleString("pt-BR")}
                      showLegend={true}
                      showGridLines={true}
                      showAnimation={true}
                    />
                  </ScrollableChart>
                ) : (
                  <EmptyChart message="Nenhum dado de ROAS disponível" />
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Campaigns Table - Melhorada */}
        <div className="space-y-3">
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCampaignFilters(true)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtrar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCampaignVisualization}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Visualizar (
              {campaignViewMode === "table"
                ? "Tabela"
                : campaignViewMode === "cards"
                  ? "Cards"
                  : "Gráfico"}
              )
            </Button>
          </div>

          {loading ? (
            <Card>
              <CardHeader>
                <CardTitle>Campanhas</CardTitle>
                <CardDescription>Carregando dados...</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : getFilteredCampaigns().length > 0 || loadingData ? (
            // `loadingData` cobre o intervalo antes da primeira busca e a troca de conta, que
            // não ligam `loading`: sem ele, a tabela dizia "nenhuma campanha" nesse meio-tempo.
            <StandardizedTable
              loading={loadingData}
              title="Campanhas"
              description={
                status?.is_connected
                  ? `Detalhamento da performance de cada campanha (${campaigns.length} campanhas)`
                  : "Detalhamento da performance de cada campanha"
              }
              columns={[
                {
                  key: "campaign",
                  header: "Campanha",
                  type: "text",
                  align: "left",
                  width: "34ch",
                  render: (_value, row) => {
                    const campaignRow = row as CampaignDisplayRow;
                    return (
                      <div className="min-w-0">
                        <div
                          className="font-medium truncate"
                          title={String(campaignRow.campaign ?? "")}
                        >
                          {String(campaignRow.campaign ?? "")}
                        </div>
                        <div
                          className="text-[11px] text-muted-foreground truncate"
                          title={String(campaignRow.campaign_type ?? "")}
                        >
                          {String(campaignRow.campaign_type ?? "")}
                        </div>
                      </div>
                    );
                  },
                },
                {
                  key: "impressions",
                  header: "Impressões",
                  type: "number",
                  align: "right",
                  width: "14ch",
                  render: (v) => formatNumber(Number(v || 0)),
                },
                {
                  key: "clicks",
                  header: "Cliques",
                  type: "number",
                  align: "right",
                  width: "12ch",
                  render: (v) => formatNumber(Number(v || 0)),
                },
                {
                  key: "ctr",
                  header: "CTR",
                  type: "percent",
                  align: "right",
                  width: "10ch",
                  render: (v) => {
                    const value = Number(v || 0);
                    return (
                      <span
                        className={
                          value > 3
                            ? "text-green-600 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {value.toFixed(1)}%
                      </span>
                    );
                  },
                },
                {
                  key: "cpc",
                  header: "CPC",
                  type: "currency",
                  align: "right",
                  width: "12ch",
                  render: (v) => formatCurrency(Number(v || 0)),
                },
                {
                  key: "conversions",
                  header: "Conversões",
                  type: "number",
                  align: "right",
                  width: "12ch",
                  render: (v) => formatNumber(Number(v || 0)),
                },
                {
                  key: "cost",
                  header: "Gasto",
                  type: "currency",
                  align: "right",
                  width: "14ch",
                  render: (v) => formatCurrency(Number(v || 0)),
                },
                {
                  key: "roas",
                  header: "ROAS",
                  type: "number",
                  align: "right",
                  width: "10ch",
                  render: (v) =>
                    v === null || v === undefined
                      ? "-"
                      : Number(v || 0).toFixed(1),
                },
                {
                  key: "status",
                  header: "Status",
                  type: "text",
                  align: "center",
                  width: "14ch",
                  render: (v) => {
                    const raw = String(v ?? "");
                    const isActive = raw === "ENABLED" || raw === "Ativa";
                    return (
                      <Badge
                        variant={isActive ? "default" : "secondary"}
                        className={`whitespace-nowrap ${isActive ? "bg-green-100 text-green-800" : ""}`}
                      >
                        {raw === "ENABLED" ? "Ativa" : raw || "Desconhecido"}
                      </Badge>
                    );
                  },
                },
                {
                  key: "_actions",
                  header: "Ações",
                  type: "text",
                  align: "center",
                  width: "18ch",
                  sortable: false,
                  render: (_v, row) => {
                    const campaign = (row as CampaignDisplayRow).__campaign;
                    return (
                      <div className="flex gap-1 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-2 py-1"
                          onClick={() => handleViewCampaignDetail(campaign)}
                        >
                          Detalhes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-2 py-1"
                        >
                          <Settings className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={getFilteredCampaigns().map((campaign: CampaignRow) => {
                const campaignName = status?.is_connected
                  ? campaign.campaign_name
                  : campaign.name;
                const campaignType = status?.is_connected
                  ? campaign.campaign_type
                  : campaign.type;
                const impressions = status?.is_connected
                  ? Number(campaign.impressions || 0)
                  : Number(campaign.impressoes || 0);
                const clicks = status?.is_connected
                  ? Number(campaign.clicks || 0)
                  : Number(campaign.cliques || 0);
                const ctr = status?.is_connected
                  ? Number(campaign.ctr || 0) * 100
                  : Number(
                      String(campaign.ctr || "0")
                        .replace("%", "")
                        .replace(",", "."),
                    ) || 0;
                const cpc = status?.is_connected
                  ? Number(campaign.average_cpc || 0)
                  : Number(
                      String(campaign.cpc || "")
                        .replace(/[^\d,.-]/g, "")
                        .replace(/\./g, "")
                        .replace(",", "."),
                    ) || 0;
                const conversions = status?.is_connected
                  ? Number(campaign.conversions || 0)
                  : Number(campaign.conversoes || 0);
                const cost = status?.is_connected
                  ? Number(campaign.cost || 0)
                  : Number(
                      String(campaign.budget || "")
                        .replace(/[^\d,.-]/g, "")
                        .replace(/\./g, "")
                        .replace(",", "."),
                    ) || 0;
                const roas = status?.is_connected
                  ? null
                  : Number(campaign.roas || 0);
                const statusValue = status?.is_connected
                  ? campaign.campaign_status
                  : campaign.status;
                return {
                  campaign: campaignName,
                  campaign_type: campaignType,
                  impressions,
                  clicks,
                  ctr,
                  cpc,
                  conversions,
                  cost,
                  roas,
                  status: statusValue,
                  __campaign: campaign,
                };
              })}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Campanhas</CardTitle>
                <CardDescription>
                  Detalhamento da performance de cada campanha
                </CardDescription>
              </CardHeader>
              <CardContent className="py-8">
                <EmptyCampaignsState />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Keywords Performance - Melhorada */}
        <div className="space-y-3">
          <div className="flex items-center justify-end">
            <Button size="sm" variant="outline" onClick={handleViewAllKeywords}>
              Ver Todas
            </Button>
          </div>

          {loading ? (
            <Card>
              <CardHeader>
                <CardTitle>Top Palavras-chave</CardTitle>
                <CardDescription>Carregando dados...</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : keywords.length > 0 || loadingData ? (
            <StandardizedTable
              loading={loadingData}
              title="Top Palavras-chave"
              description="Performance das principais palavras-chave com métricas avançadas"
              columns={[
                {
                  key: "keyword",
                  header: "Palavra-chave",
                  type: "text",
                  align: "left",
                  width: "28ch",
                  render: (value) => (
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {String(value ?? "")}
                    </span>
                  ),
                },
                {
                  key: "impressions",
                  header: "Impressões",
                  type: "number",
                  align: "right",
                  width: "14ch",
                  render: (v) => formatNumber(Number(v || 0)),
                },
                {
                  key: "clicks",
                  header: "Cliques",
                  type: "number",
                  align: "right",
                  width: "12ch",
                  render: (v) => formatNumber(Number(v || 0)),
                },
                {
                  key: "ctr",
                  header: "CTR",
                  type: "percent",
                  align: "right",
                  width: "10ch",
                  render: (v) => {
                    const value = Number(v || 0);
                    return (
                      <span
                        className={
                          value > 3
                            ? "text-green-600 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {value.toFixed(1)}%
                      </span>
                    );
                  },
                },
                {
                  key: "cpc",
                  header: "CPC",
                  type: "currency",
                  align: "right",
                  width: "12ch",
                  render: (v) => formatCurrency(Number(v || 0)),
                },
                {
                  key: "conversions",
                  header: "Conversões",
                  type: "number",
                  align: "right",
                  width: "12ch",
                  render: (v) =>
                    v === null || v === undefined
                      ? "-"
                      : formatNumber(Number(v || 0)),
                },
                {
                  key: "roas",
                  header: "ROAS",
                  type: "number",
                  align: "right",
                  width: "10ch",
                  render: (v) =>
                    v === null || v === undefined
                      ? "-"
                      : Number(v || 0).toFixed(1),
                },
                {
                  key: "quality_score",
                  header: "Quality Score",
                  type: "number",
                  align: "center",
                  width: "14ch",
                  render: (v) => {
                    const score = Number(v);
                    if (!Number.isFinite(score)) return "-";
                    return (
                      <div className="flex items-center justify-center gap-1">
                        <span className="font-medium">{score}</span>
                        {score >= 8 && (
                          <Star className="h-3 w-3 text-yellow-500 fill-current" />
                        )}
                      </div>
                    );
                  },
                },
                {
                  key: "position",
                  header: "Posição",
                  type: "number",
                  align: "right",
                  width: "10ch",
                  render: (v) => {
                    const value = Number(v);
                    if (!Number.isFinite(value)) return "-";
                    return (
                      <span
                        className={
                          value <= 2
                            ? "text-green-600 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {value.toFixed(1)}
                      </span>
                    );
                  },
                },
              ]}
              data={keywords.map((keyword: KeywordRow) => {
                const kwText = status?.is_connected
                  ? keyword.keyword_text
                  : keyword.palavra;
                const impressions = status?.is_connected
                  ? Number(keyword.impressions || 0)
                  : Number(keyword.impressoes || 0);
                const clicks = status?.is_connected
                  ? Number(keyword.clicks || 0)
                  : Number(keyword.cliques || 0);
                const ctr = status?.is_connected
                  ? Number(keyword.ctr || 0) * 100
                  : Number(
                      String(keyword.ctr || "0")
                        .replace("%", "")
                        .replace(",", "."),
                    ) || 0;
                const cpc = status?.is_connected
                  ? Number(keyword.average_cpc || 0)
                  : Number(
                      String(keyword.cpc || "")
                        .replace(/[^\d,.-]/g, "")
                        .replace(/\./g, "")
                        .replace(",", "."),
                    ) || 0;
                const conversions = status?.is_connected
                  ? null
                  : Number(keyword.conversoes || 0);
                const roas = status?.is_connected
                  ? null
                  : Number(keyword.roas || 0);
                const qualityScore = status?.is_connected
                  ? Number(keyword.quality_score)
                  : Number(keyword.qualityScore);
                const position = status?.is_connected
                  ? Number(keyword.average_position || 0)
                  : Number(keyword.posicao || 0);
                return {
                  keyword: kwText,
                  impressions,
                  clicks,
                  ctr,
                  cpc,
                  conversions,
                  roas,
                  quality_score: qualityScore,
                  position,
                };
              })}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Top Palavras-chave</CardTitle>
                <CardDescription>
                  Performance das principais palavras-chave com métricas
                  avançadas
                </CardDescription>
              </CardHeader>
              <CardContent className="py-8">
                <EmptyKeywordsState />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Modal de filtros avançados */}
        <CampaignFiltersModal />
        <CampaignNameFiltersModal
          open={showCampaignNameFilters}
          onOpenChange={setShowCampaignNameFilters}
          platform={selectedPlatform}
          customerUuid={customerUuid}
          accountId={selectedAccount || undefined}
          onSaved={loadData}
        />
      </div>
    </IntegrationErrorBoundary>
  );
};

export default GoogleAdsPage;
